import { useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { Card, Icon, Text, useTheme } from 'react-native-paper'
import { useSemanticColors } from '@/constants/SemanticColors'
import { useClockOut } from '@/hooks/useClockOut'
import { useEndBreak } from '@/hooks/useEndBreak'
import { useLiveElapsed } from '@/hooks/useLiveElapsed'
import { useStartBreak } from '@/hooks/useStartBreak'
import { formatTimeOfDay, formatTimer } from '@/lib/dates'
import { displayLocationName } from '@/lib/location-utils'
import type { TimeEntriesExpanded } from '@/types/timeTracking'
import { Button } from '@/ui/Button'
import { ActiveTimerEditSheet } from './ActiveTimerEditSheet'

interface ActiveTimerProps {
    entry?: TimeEntriesExpanded | null
    breakEntry?: TimeEntriesExpanded | null
}

export function ActiveTimer({ entry, breakEntry }: ActiveTimerProps) {
    const elapsed = useLiveElapsed(entry?.clock_in ?? '', entry ? 1000 : undefined)
    const breakElapsed = useLiveElapsed(breakEntry?.clock_in ?? '', breakEntry ? 1000 : undefined)
    const [showEditor, setShowEditor] = useState(false)
    const clockOutMutation = useClockOut()
    const startBreakMutation = useStartBreak()
    const endBreakMutation = useEndBreak()
    const theme = useTheme()
    const semanticColors = useSemanticColors()

    const handleClockOut = () => {
        if (!entry) return
        clockOutMutation.mutate({
            entryId: entry.id,
            clockIn: entry.clock_in,
        })
    }

    const handleStartBreak = () => {
        if (!entry) return
        startBreakMutation.mutate({
            parentEntry: {
                job: entry.job,
                org: entry.org,
                location: entry.location || undefined,
            },
        })
    }

    const handleEndBreak = () => {
        if (!breakEntry) return
        endBreakMutation.mutate({
            entryId: breakEntry.id,
            clockIn: breakEntry.clock_in,
        })
    }

    // Job/location come from the timer entry when present; otherwise fall back
    // to the break record itself (which carries job/location of the parent job
    // it was started from).
    const source = entry ?? breakEntry
    const jobName = source?.expand?.job?.name || 'Unknown Job'
    const locationName = displayLocationName(source?.expand?.location?.name)

    if (breakEntry) {
        return (
            <BreakActiveView
                breakElapsed={breakElapsed}
                jobElapsed={entry ? elapsed : null}
                jobName={jobName}
                locationName={locationName}
                clockInTime={entry?.clock_in}
                onEndBreak={handleEndBreak}
                isEndingBreak={endBreakMutation.isPending}
            />
        )
    }

    if (!entry) return null

    return (
        <>
            <Card style={styles.card}>
                <Card.Content>
                    <View style={styles.topRow}>
                        <View
                            style={[
                                styles.statusBadge,
                                { backgroundColor: `${semanticColors.statusActive}18` },
                            ]}
                        >
                            <View
                                style={[
                                    styles.pulse,
                                    { backgroundColor: semanticColors.statusActive },
                                ]}
                            />
                            <Text
                                variant="labelSmall"
                                style={[styles.statusText, { color: semanticColors.statusActive }]}
                            >
                                {formatTimeOfDay(entry.clock_in)}
                            </Text>
                        </View>
                        <View style={styles.detailsRow}>
                            <Icon
                                source="briefcase"
                                size={14}
                                color={theme.colors.onSurfaceVariant}
                            />
                            <Text
                                variant="bodySmall"
                                style={[
                                    styles.detailText,
                                    { color: theme.colors.onSurfaceVariant },
                                ]}
                                numberOfLines={1}
                            >
                                {jobName}
                            </Text>
                            {locationName ? (
                                <>
                                    <Icon
                                        source="map-marker"
                                        size={14}
                                        color={theme.colors.onSurfaceVariant}
                                    />
                                    <Text
                                        variant="bodySmall"
                                        style={[
                                            styles.detailText,
                                            { color: theme.colors.onSurfaceVariant },
                                        ]}
                                        numberOfLines={1}
                                    >
                                        {locationName}
                                    </Text>
                                </>
                            ) : null}
                        </View>
                    </View>
                    <Text
                        variant="displaySmall"
                        style={[styles.timer, { color: theme.colors.onSurface }]}
                    >
                        {formatTimer(elapsed)}
                    </Text>
                    <View style={styles.actionButtons}>
                        <Button
                            title="Edit"
                            mode="outlined"
                            onPress={() => setShowEditor(true)}
                            icon="note-edit-outline"
                            compact
                        />
                        <Button
                            title="Break"
                            mode="outlined"
                            onPress={handleStartBreak}
                            loading={startBreakMutation.isPending}
                            disabled={startBreakMutation.isPending}
                            icon="coffee-outline"
                            color={semanticColors.warning}
                            compact
                        />
                        <View style={styles.spacer} />
                        <Button
                            title="Clock Out"
                            mode="contained"
                            onPress={handleClockOut}
                            loading={clockOutMutation.isPending}
                            disabled={clockOutMutation.isPending}
                            icon="clock-end"
                            color={semanticColors.error}
                            compact
                        />
                    </View>
                </Card.Content>
            </Card>
            <ActiveTimerEditSheet
                visible={showEditor}
                entry={entry}
                onDismiss={() => setShowEditor(false)}
            />
        </>
    )
}

interface BreakActiveViewProps {
    breakElapsed: number
    jobElapsed: number | null
    jobName: string
    locationName?: string
    clockInTime?: string
    onEndBreak: () => void
    isEndingBreak: boolean
}

function BreakActiveView({
    breakElapsed,
    jobElapsed,
    jobName,
    locationName,
    clockInTime,
    onEndBreak,
    isEndingBreak,
}: BreakActiveViewProps) {
    const theme = useTheme()
    const semanticColors = useSemanticColors()

    return (
        <Card style={{ borderColor: semanticColors.warning, borderWidth: 2 }}>
            <Card.Content style={{ paddingVertical: 12 }}>
                <View style={styles.topRow}>
                    <View
                        style={[
                            styles.statusBadge,
                            { backgroundColor: `${semanticColors.warning}18` },
                        ]}
                    >
                        <Icon source="coffee-outline" size={14} color={semanticColors.warning} />
                        <Text
                            variant="labelSmall"
                            style={[styles.statusText, { color: semanticColors.warning }]}
                        >
                            On Break
                        </Text>
                    </View>
                    <View style={styles.detailsRow}>
                        <Icon source="briefcase" size={14} color={theme.colors.onSurfaceVariant} />
                        <Text
                            variant="bodySmall"
                            style={[styles.detailText, { color: theme.colors.onSurfaceVariant }]}
                            numberOfLines={1}
                        >
                            {jobName}
                        </Text>
                        {locationName ? (
                            <>
                                <Icon
                                    source="map-marker"
                                    size={14}
                                    color={theme.colors.onSurfaceVariant}
                                />
                                <Text
                                    variant="bodySmall"
                                    style={[
                                        styles.detailText,
                                        { color: theme.colors.onSurfaceVariant },
                                    ]}
                                    numberOfLines={1}
                                >
                                    {locationName}
                                </Text>
                            </>
                        ) : null}
                    </View>
                </View>
                <View style={styles.breakTimerRow}>
                    <View>
                        <Text
                            variant="displaySmall"
                            style={[styles.timer, { color: semanticColors.warning }]}
                        >
                            {formatTimer(breakElapsed)}
                        </Text>
                        {jobElapsed !== null && clockInTime ? (
                            <Text
                                variant="bodySmall"
                                style={{ color: theme.colors.onSurfaceVariant }}
                            >
                                Job: {formatTimer(jobElapsed)} (since {formatTimeOfDay(clockInTime)}
                                )
                            </Text>
                        ) : null}
                    </View>
                    <Button
                        title="End Break"
                        mode="contained"
                        onPress={onEndBreak}
                        loading={isEndingBreak}
                        disabled={isEndingBreak}
                        icon="coffee-off-outline"
                        color={semanticColors.warning}
                    />
                </View>
            </Card.Content>
        </Card>
    )
}

const styles = StyleSheet.create({
    card: {
        elevation: 2,
    },
    topRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        marginBottom: 8,
    },
    statusBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
        gap: 5,
    },
    pulse: {
        width: 7,
        height: 7,
        borderRadius: 4,
    },
    statusText: {
        fontWeight: 'bold',
        fontSize: 10,
    },
    detailsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        flex: 1,
        overflow: 'hidden',
    },
    detailText: {
        flexShrink: 1,
    },
    timer: {
        fontWeight: 'bold',
        fontVariant: ['tabular-nums'],
        textAlign: 'center',
        marginBottom: 8,
    },
    breakTimerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    actionButtons: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    spacer: {
        flex: 1,
    },
})
